import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  UserPlus, Check, X, Clock, Info, FileText, Warning, CheckCircle, Gear, Plus, PencilSimple, Trash, CaretDown, CaretRight, ChartLineUp, Eye,
} from "@phosphor-icons/react";
import DocPreview from "@/components/DocPreview";

const STATUS_LABEL = {
  draft: { l: "Brouillon", c: "bg-muted text-muted-foreground" },
  incomplete: { l: "Incomplète", c: "bg-accent/10 text-accent" },
  submitted: { l: "Nouveau", c: "bg-primary/10 text-primary" },
  under_review: { l: "En examen", c: "bg-accent/10 text-accent" },
  accepted: { l: "Accepté", c: "bg-secondary text-secondary-foreground" },
  conditional: { l: "Conditionnel", c: "bg-accent/20 text-accent" },
  waitlisted: { l: "Liste d'attente", c: "bg-accent/10 text-accent" },
  rejected: { l: "Refusé", c: "bg-muted text-muted-foreground" },
  archived: { l: "Archivé", c: "bg-muted text-muted-foreground" },
};
const DOC_STATUS = {
  pending: { l: "À vérifier", c: "bg-muted text-muted-foreground", icon: Clock },
  verified: { l: "Vérifié", c: "bg-secondary text-secondary-foreground", icon: CheckCircle },
  refused: { l: "Refusé", c: "bg-accent/10 text-accent", icon: Warning },
};
const REQ_EMPTY = { document_key: "", label: "", obligatory: true, active: true, grade_level_id: "" };

export default function AdmissionsAdmin() {
  const [apps, setApps] = useState([]);
  const [classes, setClasses] = useState([]);
  const [tab, setTab] = useState("submitted");
  const [openReview, setOpenReview] = useState(null); // application
  const [reviewForm, setReviewForm] = useState({ status: "accepted", decision_note: "", assign_class_group_id: "" });
  // Requirements config
  const [reqs, setReqs] = useState([]);
  const [levels, setLevels] = useState([]);
  const [reqOpen, setReqOpen] = useState(false);        // section expanded
  const [reqDialog, setReqDialog] = useState(null);     // null | 'new' | {editing req}
  const [reqForm, setReqForm] = useState(REQ_EMPTY);
  const [reqSaving, setReqSaving] = useState(false);
  // Analytics + preview
  const [analytics, setAnalytics] = useState(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(true);
  const [previewDoc, setPreviewDoc] = useState(null);

  const load = async () => {
    try {
      const [a, c, r, l, an] = await Promise.all([
        api.get("/applications"),
        api.get("/classes"),
        api.get("/admission-requirements"),
        api.get("/grade-levels"),
        api.get("/admissions/analytics").catch(() => ({ data: null })),
      ]);
      setApps(a.data);
      setClasses(c.data);
      setReqs(r.data || []);
      setLevels(l.data || []);
      setAnalytics(an.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const reviewDoc = async (aid, docKey, status, refusal_reason = "") => {
    try {
      await api.post(`/applications/${aid}/documents/${docKey}/review`, { status, refusal_reason });
      toast.success(status === "verified" ? "Pièce vérifiée" : "Pièce refusée");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openReviewFor = (app, presetStatus) => {
    setOpenReview(app);
    setReviewForm({ status: presetStatus || "accepted", decision_note: "", assign_class_group_id: "" });
  };
  const submitReview = async () => {
    if (!openReview) return;
    try {
      const payload = { ...reviewForm };
      if (!payload.assign_class_group_id) delete payload.assign_class_group_id;
      await api.post(`/applications/${openReview.id}/review`, payload);
      toast.success(`Candidature ${reviewForm.status === "accepted" ? "acceptée" : reviewForm.status}`);
      setOpenReview(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const groups = Object.fromEntries(
    Object.keys(STATUS_LABEL).map(k => [k, apps.filter(a => a.status === k)])
  );

  // ---- Requirements CRUD ----
  const openNewReq = () => { setReqForm(REQ_EMPTY); setReqDialog("new"); };
  const openEditReq = (r) => {
    setReqForm({
      document_key: r.document_key || "",
      label: r.label || "",
      obligatory: !!r.obligatory,
      active: r.active !== false,
      grade_level_id: r.grade_level_id || "",
    });
    setReqDialog(r);
  };
  const saveReq = async () => {
    if (!reqForm.document_key.trim() || !reqForm.label.trim()) {
      return toast.error("La clé et le libellé sont obligatoires");
    }
    setReqSaving(true);
    try {
      const payload = {
        document_key: reqForm.document_key.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_"),
        label: reqForm.label.trim(),
        obligatory: reqForm.obligatory,
        active: reqForm.active,
        grade_level_id: reqForm.grade_level_id || null,
      };
      if (reqDialog === "new") {
        await api.post("/admission-requirements", payload);
        toast.success("Document requis ajouté");
      } else {
        await api.patch(`/admission-requirements/${reqDialog.id}`, payload);
        toast.success("Document requis mis à jour");
      }
      setReqDialog(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReqSaving(false); }
  };
  const deleteReq = async (r) => {
    if (!window.confirm(`Supprimer "${r.label}" ?`)) return;
    try {
      await api.delete(`/admission-requirements/${r.id}`);
      toast.success("Supprimé");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleReqActive = async (r) => {
    try {
      await api.patch(`/admission-requirements/${r.id}`, {
        document_key: r.document_key, label: r.label, obligatory: r.obligatory,
        grade_level_id: r.grade_level_id || null, active: !r.active,
      });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const levelName = (gid) => levels.find(l => l.id === gid)?.name || "";

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="mb-8">
        <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Admissions</div>
        <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
          <UserPlus size={32} weight="duotone" className="text-primary" />
          Candidatures reçues
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Examinez et validez les candidatures en ligne.</p>
      </header>

      {/* --- Analytics dashboard --- */}
      {analytics && (
        <Card className="mb-6 overflow-hidden" data-testid="analytics-section">
          <button onClick={() => setAnalyticsOpen(o => !o)}
                  className="w-full flex items-center justify-between p-4 hover:bg-muted/40 transition"
                  data-testid="btn-toggle-analytics">
            <div className="flex items-center gap-3">
              <ChartLineUp size={22} weight="duotone" className="text-secondary" />
              <div className="text-left">
                <div className="font-heading font-semibold">Tableau de bord des candidatures</div>
                <div className="text-xs text-muted-foreground">
                  Entonnoir soumission → vérification → admission → inscription
                  {analytics.conversion_rate !== undefined && ` · taux de conversion global ${analytics.conversion_rate}%`}
                </div>
              </div>
            </div>
            {analyticsOpen ? <CaretDown size={18} /> : <CaretRight size={18} />}
          </button>
          {analyticsOpen && (
            <div className="border-t p-5 space-y-6">
              {/* Funnel */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-3">Entonnoir des admissions</div>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                  {[
                    { k: "submitted",     l: "Reçues",       v: analytics.funnel.submitted,    color: "bg-primary/10 text-primary" },
                    { k: "under_review",  l: "En cours",     v: analytics.funnel.under_review, color: "bg-accent/10 text-accent" },
                    { k: "verified",      l: "Dossiers OK",  v: analytics.funnel.verified,     color: "bg-accent/20 text-accent" },
                    { k: "accepted",      l: "Acceptées",    v: analytics.funnel.accepted,     color: "bg-secondary/15 text-secondary" },
                    { k: "enrolled",      l: "Inscrits",     v: analytics.funnel.enrolled,     color: "bg-secondary text-secondary-foreground" },
                  ].map((s, i) => {
                    const prev = i > 0 ? [analytics.funnel.submitted, analytics.funnel.under_review, analytics.funnel.verified, analytics.funnel.accepted][i - 1] : null;
                    const drop = prev && prev > 0 ? Math.round(100 * s.v / prev) : null;
                    return (
                      <div key={s.k} className={`rounded-lg p-3 ${s.color}`} data-testid={`funnel-${s.k}`}>
                        <div className="text-3xl font-bold font-heading tabular-nums">{s.v}</div>
                        <div className="text-xs uppercase tracking-wider opacity-80 mt-1">{s.l}</div>
                        {drop !== null && <div className="text-[10px] opacity-70 mt-0.5">{drop}% du précédent</div>}
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-4 mt-3 text-xs">
                  <div><span className="text-muted-foreground">Taux d'acceptation :</span> <b>{analytics.acceptance_rate}%</b></div>
                  <div><span className="text-muted-foreground">Taux d'inscription :</span> <b>{analytics.conversion_rate}%</b></div>
                </div>
              </div>
              {/* Monthly trend */}
              {analytics.monthly?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Tendance mensuelle (12 mois)</div>
                  <div className="flex gap-1 items-end h-24">
                    {analytics.monthly.map(m => {
                      const max = Math.max(...analytics.monthly.map(x => x.received), 1);
                      const rH = (m.received / max) * 100;
                      const aH = (m.accepted / max) * 100;
                      return (
                        <div key={m.month} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${m.month} · reçues=${m.received}, acceptées=${m.accepted}, refusées=${m.rejected}`}>
                          <div className="w-full flex flex-col justify-end h-20 gap-0.5">
                            <div className="w-full bg-primary/25 rounded-t" style={{ height: `${rH}%` }} />
                            <div className="w-full bg-secondary/60 rounded-t" style={{ height: `${aH}%` }} />
                          </div>
                          <div className="text-[9px] text-muted-foreground truncate">{m.month.slice(5)}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex gap-3 text-[10px] text-muted-foreground mt-1">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 bg-primary/25 rounded-sm" /> Reçues</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 bg-secondary/60 rounded-sm" /> Acceptées</span>
                  </div>
                </div>
              )}
              {/* Per level */}
              {analytics.by_level?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Par niveau scolaire</div>
                  <div className="space-y-1">
                    {analytics.by_level.slice(0, 8).map(l => {
                      const rate = l.received > 0 ? Math.round(100 * l.accepted / l.received) : 0;
                      return (
                        <div key={l.level_id} className="flex items-center gap-3 text-xs" data-testid={`level-row-${l.level_id}`}>
                          <div className="w-32 truncate">{l.level_name}</div>
                          <div className="flex-1 bg-muted h-2 rounded overflow-hidden">
                            <div className="h-full bg-secondary" style={{ width: `${rate}%` }} />
                          </div>
                          <div className="w-28 text-right text-muted-foreground">{l.accepted}/{l.received} <span className="tabular-nums">({rate}%)</span></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* --- Requirements config section --- */}
      <Card className="mb-6 overflow-hidden" data-testid="reqs-section">
        <button
          onClick={() => setReqOpen(o => !o)}
          className="w-full flex items-center justify-between p-4 hover:bg-muted/40 transition"
          data-testid="btn-toggle-reqs">
          <div className="flex items-center gap-3">
            <Gear size={22} weight="duotone" className="text-primary" />
            <div className="text-left">
              <div className="font-heading font-semibold">Configuration des documents requis</div>
              <div className="text-xs text-muted-foreground">
                {reqs.length} document{reqs.length !== 1 ? "s" : ""} configuré{reqs.length !== 1 ? "s" : ""}
                {" — "}visible sur la page de candidature publique et sur le portail de suivi.
              </div>
            </div>
          </div>
          {reqOpen ? <CaretDown size={18} /> : <CaretRight size={18} />}
        </button>
        {reqOpen && (
          <div className="border-t p-4 space-y-3">
            <div className="flex items-center justify-end">
              <Button size="sm" onClick={openNewReq} data-testid="btn-new-req">
                <Plus size={14} className="mr-1" /> Ajouter un document requis
              </Button>
            </div>

            {reqs.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground rounded-lg border border-dashed">
                Aucun document requis configuré. Ajoutez-en un pour que les familles voient les pièces à fournir dès l'étape de candidature.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b">
                      <th className="px-3 py-2 font-semibold">Clé technique</th>
                      <th className="px-3 py-2 font-semibold">Libellé</th>
                      <th className="px-3 py-2 font-semibold">Niveau scolaire</th>
                      <th className="px-3 py-2 font-semibold">Statut</th>
                      <th className="px-3 py-2 font-semibold">Actif</th>
                      <th className="px-3 py-2 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reqs.map((r, i) => (
                      <tr key={r.id} className={`border-b last:border-0 ${i % 2 ? "bg-muted/30" : ""}`} data-testid={`req-row-${r.document_key}`}>
                        <td className="px-3 py-2 font-mono text-xs">{r.document_key}</td>
                        <td className="px-3 py-2">{r.label}</td>
                        <td className="px-3 py-2 text-xs">
                          {r.grade_level_id
                            ? levelName(r.grade_level_id) || <span className="text-muted-foreground italic">niveau inconnu</span>
                            : <span className="text-muted-foreground italic">Tous niveaux</span>}
                        </td>
                        <td className="px-3 py-2">
                          <Badge className={r.obligatory ? "bg-accent/10 text-accent" : "bg-muted text-muted-foreground"}>
                            {r.obligatory ? "Obligatoire" : "Facultatif"}
                          </Badge>
                        </td>
                        <td className="px-3 py-2">
                          <Switch checked={!!r.active} onCheckedChange={() => toggleReqActive(r)} data-testid={`switch-active-${r.document_key}`} />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button variant="ghost" size="icon" onClick={() => openEditReq(r)} data-testid={`btn-edit-req-${r.document_key}`}><PencilSimple size={15} /></Button>
                          <Button variant="ghost" size="icon" onClick={() => deleteReq(r)} data-testid={`btn-del-req-${r.document_key}`}><Trash size={15} className="text-destructive" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="tabs-admissions">
          {Object.entries(STATUS_LABEL).map(([k, s]) => (
            <TabsTrigger key={k} value={k} data-testid={`tab-adm-${k}`}>
              {s.l} <span className="ml-1.5 text-[10px] opacity-70">({groups[k].length})</span>
            </TabsTrigger>
          ))}
        </TabsList>
        {Object.keys(STATUS_LABEL).map(k => (
          <TabsContent key={k} value={k} className="mt-6">
            {groups[k].length === 0 ? (
              <Card className="p-10 text-center text-sm text-muted-foreground">Aucune candidature dans ce statut.</Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {groups[k].map(a => (
                  <Card key={a.id} className="p-5" data-testid={`app-${a.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-heading font-semibold text-lg">{a.child_last_name} {a.child_first_name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Né(e) le {a.child_birth_date} · {a.child_gender === "F" ? "♀ Fille" : "♂ Garçon"}
                          {a.grade_level_name ? ` · souhaite ${a.grade_level_name}` : ""}
                        </div>
                      </div>
                      <Badge className={STATUS_LABEL[a.status]?.c || ""}>{STATUS_LABEL[a.status]?.l || a.status}</Badge>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Parent/Tuteur</div>
                        <div className="mt-1 font-medium">{a.guardian_last_name} {a.guardian_first_name}</div>
                        <div className="text-muted-foreground">{a.guardian_email}</div>
                        <div className="text-muted-foreground">{a.guardian_phone}</div>
                        <div className="text-[10px] text-muted-foreground capitalize">Lien: {a.guardian_relation}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Historique</div>
                        <div className="mt-1">{a.previous_school || "—"}</div>
                        <div className="mt-2 flex items-center gap-1 text-[10px] text-muted-foreground">
                          <Clock size={11} /> Reçue le {new Date(a.created_at).toLocaleDateString("fr-FR")}
                        </div>
                      </div>
                    </div>
                    {a.additional_info && (
                      <div className="mt-3 text-xs bg-muted/40 rounded p-2 border-l-2 border-primary/50">{a.additional_info}</div>
                    )}
                    {a.decision_note && (
                      <div className="mt-3 text-xs bg-secondary/5 rounded p-2 border-l-2 border-secondary flex gap-2">
                        <Info size={13} className="shrink-0 mt-0.5" />
                        <div><span className="font-semibold">Décision : </span>{a.decision_note}</div>
                      </div>
                    )}
                    {(a.documents || []).length > 0 && (
                      <div className="mt-3 border-t pt-2">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Pièces jointes ({a.documents.length})</div>
                        <div className="space-y-1">
                          {a.documents.map((d, i) => {
                            const st = DOC_STATUS[d.status || "pending"];
                            return (
                              <div key={i} className="flex items-center justify-between gap-2 text-xs p-1.5 rounded bg-muted/40" data-testid={`app-${a.id}-doc-${i}`}>
                                <button onClick={() => setPreviewDoc(d)}
                                        className="flex items-center gap-1 min-w-0 flex-1 hover:underline text-left"
                                        data-testid={`btn-preview-${a.id}-${d.key}`}>
                                  <FileText size={11} /> <span className="truncate">{d.label || d.key}</span>
                                </button>
                                <Badge className={`${st.c} text-[9px] shrink-0`}>{st.l}</Badge>
                                <Button size="icon" variant="ghost" className="h-5 w-5" title="Prévisualiser"
                                        onClick={() => setPreviewDoc(d)}
                                        data-testid={`btn-eye-${a.id}-${d.key}`}>
                                  <Eye size={11} className="text-primary" />
                                </Button>
                                {d.status !== "verified" && (
                                  <Button size="icon" variant="ghost" className="h-5 w-5" title="Vérifier"
                                          onClick={() => reviewDoc(a.id, d.key, "verified")}
                                          data-testid={`btn-verify-${a.id}-${d.key}`}>
                                    <Check size={11} className="text-secondary" />
                                  </Button>
                                )}
                                {d.status !== "refused" && (
                                  <Button size="icon" variant="ghost" className="h-5 w-5" title="Refuser"
                                          onClick={() => {
                                            const reason = window.prompt("Motif du refus (visible par le candidat) :", d.refusal_reason || "Document illisible");
                                            if (reason !== null) reviewDoc(a.id, d.key, "refused", reason);
                                          }}
                                          data-testid={`btn-refuse-${a.id}-${d.key}`}>
                                    <X size={11} className="text-destructive" />
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {a.status === "submitted" || a.status === "under_review" || a.status === "waitlisted" ? (
                      <div className="mt-4 flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => openReviewFor(a, "rejected")} data-testid={`btn-reject-${a.id}`}>
                          <X size={14} className="mr-1" /> Refuser
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openReviewFor(a, "waitlisted")} data-testid={`btn-waitlist-${a.id}`}>
                          Liste d'attente
                        </Button>
                        <Button size="sm" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground" onClick={() => openReviewFor(a, "accepted")} data-testid={`btn-accept-${a.id}`}>
                          <Check size={14} className="mr-1" /> Accepter
                        </Button>
                      </div>
                    ) : null}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Review dialog */}
      <Dialog open={!!openReview} onOpenChange={(v) => { if (!v) setOpenReview(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Décision — {openReview?.child_first_name} {openReview?.child_last_name}</DialogTitle>
            <DialogDescription>
              {reviewForm.status === "accepted" ? "Un compte parent + élève seront créés automatiquement." :
               reviewForm.status === "rejected" ? "La candidature sera archivée avec cette note." :
               "La candidature sera mise en liste d'attente."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label>Statut final</Label>
              <Select value={reviewForm.status} onValueChange={v => setReviewForm({ ...reviewForm, status: v })}>
                <SelectTrigger data-testid="select-review-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="accepted">Accepté</SelectItem>
                  <SelectItem value="conditional">Admission conditionnelle</SelectItem>
                  <SelectItem value="waitlisted">Liste d'attente</SelectItem>
                  <SelectItem value="rejected">Refusé</SelectItem>
                  <SelectItem value="under_review">En examen</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {reviewForm.status === "accepted" && (
              <div>
                <Label>Affecter à une classe</Label>
                <Select value={reviewForm.assign_class_group_id || "none"} onValueChange={v => setReviewForm({ ...reviewForm, assign_class_group_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="select-review-class"><SelectValue placeholder="Non affecté pour l'instant" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">À décider plus tard</SelectItem>
                    {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Note à la famille (facultative)</Label>
              <Textarea rows={3} value={reviewForm.decision_note} onChange={e => setReviewForm({ ...reviewForm, decision_note: e.target.value })} data-testid="input-review-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenReview(null)}>Annuler</Button>
            <Button onClick={submitReview} data-testid="btn-save-review">Valider la décision</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Requirement add/edit dialog --- */}
      <Dialog open={!!reqDialog} onOpenChange={(v) => { if (!v) setReqDialog(null); }}>
        <DialogContent data-testid="dialog-req">
          <DialogHeader>
            <DialogTitle>{reqDialog === "new" ? "Nouveau document requis" : "Modifier le document requis"}</DialogTitle>
            <DialogDescription>
              Les documents actifs seront proposés aux familles pendant la candidature et sur leur portail de suivi.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label>Libellé affiché aux familles <span className="text-accent">*</span></Label>
              <Input value={reqForm.label} onChange={e => setReqForm({ ...reqForm, label: e.target.value })}
                     placeholder="Ex : Extrait de naissance"
                     data-testid="input-req-label" />
            </div>
            <div>
              <Label>Clé technique <span className="text-accent">*</span></Label>
              <Input value={reqForm.document_key}
                     onChange={e => setReqForm({ ...reqForm, document_key: e.target.value })}
                     placeholder="birth_certificate, photo_id, report_card…"
                     disabled={reqDialog !== "new"}
                     data-testid="input-req-key" />
              <div className="text-[11px] text-muted-foreground mt-1">Sera normalisée en minuscules avec underscores. Non modifiable après création.</div>
            </div>
            <div>
              <Label>Niveau scolaire concerné</Label>
              <Select value={reqForm.grade_level_id || "all"} onValueChange={v => setReqForm({ ...reqForm, grade_level_id: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="select-req-level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les niveaux</SelectItem>
                  {levels.map(l => (
                    <SelectItem key={l.id} value={l.id}>{l.name}{l.code ? ` (${l.code})` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-muted-foreground mt-1">Si un niveau est choisi, ce document ne sera demandé qu'aux candidats pour ce niveau.</div>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <div className="font-medium text-sm">Obligatoire</div>
                <div className="text-xs text-muted-foreground">La famille doit fournir ce document pour compléter le dossier.</div>
              </div>
              <Switch checked={reqForm.obligatory} onCheckedChange={v => setReqForm({ ...reqForm, obligatory: v })} data-testid="switch-req-obligatory" />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <div className="font-medium text-sm">Actif</div>
                <div className="text-xs text-muted-foreground">Visible sur la page publique et le portail de suivi.</div>
              </div>
              <Switch checked={reqForm.active} onCheckedChange={v => setReqForm({ ...reqForm, active: v })} data-testid="switch-req-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReqDialog(null)}>Annuler</Button>
            <Button onClick={saveReq} disabled={reqSaving} data-testid="btn-save-req">
              {reqSaving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DocPreview doc={previewDoc} onClose={() => setPreviewDoc(null)} />
    </div>
  );
}
